import { useEffect } from 'react'

interface Sentinel { release: () => Promise<void> }
type WakeLockNavigator = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<Sentinel> } }

/**
 * Keeps the screen on while the calling component is mounted (cooking mode,
 * feature spec 04). The browser drops a wake lock whenever the page is hidden,
 * so it is asked for again when the page comes back. Failures (no support,
 * battery saver, not allowed) are ignored: a screen that sleeps is the
 * fallback, not an error.
 */
export function useWakeLock() {
  useEffect(() => {
    const nav = navigator as WakeLockNavigator
    if (!nav.wakeLock) return
    let sentinel: Sentinel | null = null
    let active = true

    const acquire = async () => {
      try {
        const s = await nav.wakeLock!.request('screen')
        if (active) sentinel = s
        else s.release().catch(() => {})
      } catch { /* not allowed now: the screen may sleep */ }
    }
    const onVisible = () => { if (document.visibilityState === 'visible') acquire() }

    acquire()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      active = false
      document.removeEventListener('visibilitychange', onVisible)
      sentinel?.release().catch(() => {})
    }
  }, [])
}
